import React, { useCallback, useEffect, useState } from 'react'
import { SkeletonList } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  CardTitle,
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/recordsService'
import { parseUtcDate } from '../../utils/dates'

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fileIcon(mimeType) {
  const type = `${mimeType || ''}`.toLowerCase()
  if (type.startsWith('image/')) return '🖼️'
  if (type.includes('pdf')) return '📄'
  return '📎'
}

export default function SharedWithMePage() {
  const { currentProfile } = usePatient()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [openingId, setOpeningId] = useState(null)
  const [openError, setOpenError] = useState(null)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const res = await svc.listSharedDocuments(currentProfile)
      setData(res)
    } catch (e) {
      // Degrade to the empty state (e.g. before the backend endpoint is deployed) rather than
      // leaving an unhandled rejection.
      setData({ items: [] })
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setData(null)
    refresh()
  }, [refresh])

  const handleOpen = useCallback(
    async (item) => {
      setOpeningId(item.id)
      setOpenError(null)
      try {
        const objectUrl = await svc.openSharedDocument(currentProfile, item.id)
        if (objectUrl) {
          window.open(objectUrl, '_blank', 'noopener')
          // The tab has taken a reference by now; release ours.
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
        }
      } catch (e) {
        setOpenError(item.id)
      } finally {
        setOpeningId(null)
      }
    },
    [currentProfile],
  )

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/records">Back to Records</BackLink>
        <div>
          <Pill>Records</Pill>
          <PageHeading style={{ marginTop: 12 }}>Shared with me</PageHeading>
          <PageSubheading>
            Files your practitioner shared with you — diet plans, scans, instructions and more.
          </PageSubheading>
        </div>

        {loading && !data ? (
          <SkeletonList count={3} />
        ) : !data?.items?.length ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>Nothing shared yet.</p>
            <Muted>When a practitioner shares a document with you, it will appear here.</Muted>
          </div>
        ) : (
          <Stack $gap="12px">
            {data.items.map((item) => {
              const sharedOn = item.sharedAt ? parseUtcDate(item.sharedAt) : null
              return (
                <Card key={item.id}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ fontSize: 26, lineHeight: 1 }} aria-hidden="true">
                      {fileIcon(item.mimeType)}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <CardTitle style={{ wordBreak: 'break-word' }}>{item.fileName}</CardTitle>
                      <Muted>
                        {[
                          item.practitionerName,
                          item.clinicBrandName,
                          sharedOn
                            ? sharedOn.toLocaleDateString([], {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : null,
                          formatBytes(item.sizeBytes),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Muted>
                      {item.caption && (
                        <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.5 }}>
                          {item.caption}
                        </p>
                      )}
                      {openError === item.id && (
                        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#dc2626' }}>
                          Couldn&apos;t open this document. Please try again.
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleOpen(item)}
                      disabled={openingId === item.id}
                      style={{
                        flexShrink: 0,
                        border: '1px solid rgba(67,56,202,0.35)',
                        background: '#fff',
                        color: '#4338ca',
                        borderRadius: 10,
                        padding: '8px 14px',
                        fontWeight: 600,
                        cursor: openingId === item.id ? 'wait' : 'pointer',
                      }}
                    >
                      {openingId === item.id ? 'Opening…' : 'Open'}
                    </button>
                  </div>
                </Card>
              )
            })}
          </Stack>
        )}
      </Stack>
    </ContentMaxWidth>
  )
}
